import { RootNodesForDimensionsResponse } from '@amzn/gft-dimsum-service';
import { DECKLINE_KIND_BY_LABEL, DECKLINE_KIND_LABELS } from '@constants/enumLabels';
import { DENOMINATOR_DATASETS, EXCLUDED_DECKLINES_FOR_DENOMINATORS, KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import {
  generateFormula,
  isSupportedFormula,
  mergePageDriversToParsedFormula,
  splitRawFormula,
} from '@deckManagement/components/Common/CalculatedKPIBuilder/utils/formulaHelpers';
import { UpdateDecklineType } from '@schema/Deckline.schema';
import {
  BlanklineType,
  CalculatedKPIValues,
  DatasetSource,
  Deckline,
  GalaxyKPIDrivers,
  KPIType,
} from '@services/api/deckManagement/graphql/schema/API';
import { parseIdFromKey } from '@utils/deckHelpers';
import { changeEntryField, changeMultipleEntryFields } from '@utils/deckManagementEntityHelpers';
import {
  asGalaxyDrivers,
  asKPIDrivers,
  datasetSourceFromDrivers,
  hasDriverMismatch,
  sanitizeKPIDrivers,
} from '@utils/kpiDriverHelpers';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { DecklineRowData, PartialKPIDrivers } from 'types/Page.types';
import { AnyKPIDrivers, PageSidePanelDropDown } from 'types/PageSidePanel.types';

/**
 * Returns the KPI name from a deckline.
 *
 * Legacy note: kpi_name previously contained embedded footnotes and indent spaces.
 * Those are now stored in separate fields (`footnotes` and `kpi_name_indent_level`),
 * so this simply returns kpi_name directly.
 *
 * @param deckline - The Deckline object
 * @returns The KPI name
 */
export const extractActualKpiName = (deckline: Deckline | EnhancedDeckline): string => {
  return deckline.kpi_name ?? '';
};

/**
 * Merges actual KPI name with footnotes to create the full kpi_name
 * @param deckline - The EnhancedDeckline object
 * @returns The full KPI name with footnotes appended
 */
export const mergeKpiNameWithFootnotes = (deckline: EnhancedDeckline): string => {
  const actualKpiName = deckline.metadata?.actual_kpi_name || '';
  if (!deckline.footnotes || deckline.footnotes.length === 0) return actualKpiName;
  const footnotesString = deckline.footnotes.join(',');
  return `${actualKpiName}${footnotesString}`;
};

/**
 * Calculates start and end row numbers from child rows
 * @param childRows - Array of deckline row data
 * @returns Object with min and max row numbers, or null if no valid rows
 */
export const getStartEndSubtotalRows = (childRows: DecklineRowData[]): { min: number; max: number } | null => {
  const rowNumbers = childRows.map((row) => Number(row.rowNumber)).filter((n) => !Number.isNaN(n));
  if (rowNumbers.length > 0) {
    return {
      min: Math.min(...rowNumbers),
      max: Math.max(...rowNumbers),
    };
  }
  return null;
};

/**
 * Finds the subtotal_parent for a row by looking backwards through decklines
 * @param decklines - Array of decklines
 * @param currentIndex - Index of the current row
 * @returns The subtotal_parent value or null if not found
 */
export const findSubtotalParent = (decklines: EnhancedDeckline[], currentIndex: number): string | null => {
  if (currentIndex <= 0) return null;

  let checkIndex = currentIndex - 1;
  while (checkIndex >= 0) {
    const checkRow = decklines[checkIndex];
    if (checkRow.blankline_type?.startsWith('BLANKLINE')) {
      checkIndex -= 1;
    } else {
      return checkRow.subtotal_parent || null;
    }
  }
  return null;
};

/**
 * Determines the "type" of deckline (Blankline, Base KPI, Calculated KPI, etc.)
 *
 * Prefers the BE-served `deckline_kind` when present: the BE derivation and this attribute sniff agree
 * on served rows, so honoring the field is behavior-preserving and stays inert until rows actually carry it.
 * Rows predating the BE `deckline_kind` will fall through to the legacy attribute sniff below.
 * @param deckline
 */
export const determineDecklineType = (deckline?: EnhancedDeckline | Deckline | null): PageSidePanelDropDown => {
  if (deckline?.deckline_kind) {
    return DECKLINE_KIND_LABELS[deckline.deckline_kind];
  }

  const isBlankLine = !isNil(deckline?.blankline_type) && deckline?.blankline_type.startsWith('BLANKLINE');
  const isAdjustment = deckline?.is_calculated_kpi === CalculatedKPIValues.I;
  const isCalculatedAdjustment = isAdjustment && deckline?.adjcalcflag;
  const isCalculatedKPI = deckline?.is_calculated_kpi === CalculatedKPIValues.Y;
  const isSubtotal = !isNil(deckline?.subtotal_formula);

  if (isBlankLine) {
    return PageSidePanelDropDown.BLANK_LINE;
  }
  if (isCalculatedAdjustment) {
    return PageSidePanelDropDown.CALCULATED_ADJUSTMENT;
  }
  if (isAdjustment) {
    return PageSidePanelDropDown.ADJUSTMENT;
  }
  if (isCalculatedKPI) {
    return PageSidePanelDropDown.CALCULATED_KPI;
  }
  if (isSubtotal) {
    return PageSidePanelDropDown.SUBTOTAL;
  }
  // Default to Base KPI and reset subtotal creation flag
  return PageSidePanelDropDown.BASE_KPI;
};

/**
 * Determines the denominator type for a deckline based on its properties.
 * Returns NONE if no denominator is configured, CALCULATED_KPI if it uses a formula, otherwise BASE_KPI.
 * @param deckline - The deckline to analyze
 */
export const determineDenominatorType = (deckline?: EnhancedDeckline | Deckline | null): PageSidePanelDropDown => {
  const kpiType = denominatorKpiTypeOf(deckline);
  if (kpiType === KPIType.CALCULATED) return PageSidePanelDropDown.CALCULATED_KPI;
  if (kpiType === KPIType.BASE) return PageSidePanelDropDown.BASE_KPI;
  return PageSidePanelDropDown.NONE;
};

/**
 * A row's denominator type. `denominator_kpi_type` decides whenever the row carries it, null included:
 * the backend serves it on every row, and a row switched to "Not Applicable" holds null. Only a row
 * without the field falls back to the deprecated flag, then to BASE when a stored root or id remains.
 * Null when the row has no denominator.
 * @param deckline - The deckline to read
 */
export const denominatorKpiTypeOf = (deckline?: EnhancedDeckline | Deckline | null): KPIType | null => {
  if (deckline?.denominator_kpi_type === KPIType.BASE || deckline?.denominator_kpi_type === KPIType.CALCULATED) {
    return deckline.denominator_kpi_type;
  }
  if (deckline && 'denominator_kpi_type' in deckline && deckline.denominator_kpi_type !== undefined) {
    return null;
  }
  if (deckline?.denominator_is_calculated_kpi === CalculatedKPIValues.Y) return KPIType.CALCULATED;
  if (deckline?.denominator_is_calculated_kpi === CalculatedKPIValues.N) return KPIType.BASE;
  if (deckline?.denominator_root_kpi_id || !isEmpty(deckline?.denominator_kpi_id_for_pct)) return KPIType.BASE;
  return null;
};

/**
 * Options for updateDecklineType when switching to types that require additional context.
 * @property currentPageKPIDrivers - Required for BASE_KPI to initialize null (cascading) drivers
 * @property prevDecklines - Required for BASE_KPI and CALCULATED_KPI to find subtotal_parent
 * @property decklineIndex - Index of the deckline in prevDecklines, used with findSubtotalParent
 */
interface UpdateDecklineTypeOptions {
  currentPageKPIDrivers?: AnyKPIDrivers | null;
  prevDecklines?: EnhancedDeckline[];
  decklineIndex?: number;
}

/**
 * Updates a deckline's fields to match the given type. This is the single source of truth
 * for which fields need to change when a deckline's type is switched.
 *
 * Also sets `deckline_type` on the returned deckline so downstream consumers
 * (e.g. SegmentsCellRenderer) can check the type without re-deriving it, and keeps the served
 * `deckline_kind` in sync so `determineDecklineType` (which prefers that field) doesn't resolve a
 * stale kind after the type changes.
 *
 * @param deckline - The deckline to update (will be deep-cloned internally)
 * @param newType - The target PageSidePanelDropDown type
 * @param options - Additional context needed for certain type transitions
 * @returns The updated deckline with all type-related fields set
 */
export const updateDecklineType = (
  deckline: EnhancedDeckline,
  newType: PageSidePanelDropDown,
  options: UpdateDecklineTypeOptions = {},
): EnhancedDeckline => {
  const { currentPageKPIDrivers, prevDecklines, decklineIndex } = options;
  let updated = cloneDeep(deckline);

  // Fields that become stale on any type switch. Does not include structural, formatting, or user-entered fields.
  const defaultFieldResets: Partial<EnhancedDeckline> = {
    root_kpi_id: null,
    kpi_variant_id: null,
    display_name: null,
    adjcalcflag: false,
    is_exception: null,
    exceptioncalculatedkpiflag: false,
    kpi_formula: null,
    subtotal_formula: null,
    deckline_drivers: null,
    blankline_type: null,
  };

  if (newType === PageSidePanelDropDown.BLANK_LINE) {
    updated = changeMultipleEntryFields(updated, {
      ...defaultFieldResets,
      kpi_id: '|BlankLine|',
      blankline_type: BlanklineType.BLANKLINE,
      is_calculated_kpi: CalculatedKPIValues.NA,
      denominator_kpi_type: null,
      denominator_root_kpi_id: null,
      denominator_kpi_variant_id: null,
      denominator_kpi_id_for_pct: null,
      denominator_kpi_formula: null,
      denominator_kpi_id_drivers: null,
    });
  } else if (newType === PageSidePanelDropDown.BASE_KPI) {
    const nullDrivers = currentPageKPIDrivers
      ? (Object.keys(currentPageKPIDrivers).reduce((acc, key) => {
          (acc as Record<string, unknown>)[key] =
            key === '__typename' || key === 'datasetSource'
              ? (currentPageKPIDrivers as Record<string, unknown>)[key]
              : null;
          return acc;
        }, {} as AnyKPIDrivers) as AnyKPIDrivers)
      : null;
    updated = changeMultipleEntryFields(updated, {
      ...defaultFieldResets,
      kpi_id: '',
      is_calculated_kpi: CalculatedKPIValues.N,
      deckline_drivers: asKPIDrivers(nullDrivers),
    });

    if (!updated.subtotal_parent && prevDecklines && decklineIndex !== undefined) {
      updated = changeEntryField(updated, 'subtotal_parent', findSubtotalParent(prevDecklines, decklineIndex));
    }
  } else if (newType === PageSidePanelDropDown.CALCULATED_KPI) {
    // No baked `kpi_id` seed: a new calc row carries its expression in `calc_members` and the backend
    // derives the id (one per column) on save. See design §5.5 / R8.2.
    updated = changeMultipleEntryFields(updated, {
      ...defaultFieldResets,
      kpi_id: '',
      is_calculated_kpi: CalculatedKPIValues.Y,
      kpi_formula: '',
    });

    if (!updated.subtotal_parent && prevDecklines && decklineIndex !== undefined) {
      updated = changeEntryField(updated, 'subtotal_parent', findSubtotalParent(prevDecklines, decklineIndex));
    }
  } else if (newType === PageSidePanelDropDown.SUBTOTAL) {
    updated = changeMultipleEntryFields(updated, {
      ...defaultFieldResets,
      kpi_id: '|BlankLine|',
      is_calculated_kpi: CalculatedKPIValues.NA,
      subtotal_formula: '',
    });
  } else if (newType === PageSidePanelDropDown.ADJUSTMENT) {
    updated = changeMultipleEntryFields(updated, {
      ...defaultFieldResets,
      is_calculated_kpi: CalculatedKPIValues.I,
      subtotal_formula: '',
    });
  } else if (newType === PageSidePanelDropDown.CALCULATED_ADJUSTMENT) {
    updated = changeMultipleEntryFields(updated, {
      ...defaultFieldResets,
      adjcalcflag: true,
      is_calculated_kpi: CalculatedKPIValues.I,
      subtotal_formula: '',
    });
  }

  updated.metadata.decklineType = newType;
  updated = changeEntryField(updated, 'deckline_type', newType);
  // Keep the served `deckline_kind` in sync with the new type. `determineDecklineType` prefers this
  // field, so leaving the old value here would make a re-derivation (e.g. on reselecting this deckline)
  // resolve the stale kind and render the wrong side-panel sub-components. (The kind sent on save is
  // re-derived at save-prep in DecklinesContext, so it doesn't depend on this value.)
  updated = changeEntryField(updated, 'deckline_kind', DECKLINE_KIND_BY_LABEL[newType] ?? null);
  return updated;
};

/**
 * Sanitizes Deckline data.
 *
 * Intended to be used in DecklinesContextProvider to sanitize incoming deckline data before use.
 * @todo Add more field sanitization as needed.
 * @param decklineData - The raw deckline data to sanitize
 * @returns A sanitized version of the Deckline data
 * @see {@link sanitizeKPIDrivers} for KPIDriver sanitization logic
 * @see {@link Deckline} for the Deckline interface definition
 * @see {@link DecklinesContextProvider} for primary usage.
 */
export const sanitizeDecklineData = (decklineData: Deckline): Deckline => {
  return {
    ...decklineData,
    deckline_drivers: sanitizeKPIDrivers(decklineData.deckline_drivers),
  };
};

/**
 * Switches a deckline's denominator to `newType`, setting that kind's fields and clearing the others.
 *
 * - NONE: no type; every denominator field cleared (the backend clears them too).
 * - BASE: the type and a root (by default the row's own root); the backend derives the id and drivers, so
 *   none are authored here and the variant is picked in the tab.
 * - CALCULATED: the type, a calc id and a formula, seeded from the row's own formula when it has one.
 * @param deckline - The deckline to update
 * @param newType - The denominator kind to switch to
 * @param rootNodes - DimSum root nodes, for the calculated formula
 * @param currentPageKPIDrivers - The page's Galaxy drivers, for the calculated formula's account
 */
export const updateDecklineDenominatorType = (
  deckline: EnhancedDeckline,
  newType: PageSidePanelDropDown,
  rootNodes: RootNodesForDimensionsResponse,
  currentPageKPIDrivers?: GalaxyKPIDrivers | null,
) => {
  let clonedDeckline = cloneDeep(deckline);
  const wasBase = denominatorKpiTypeOf(clonedDeckline) === KPIType.BASE;

  if (newType === PageSidePanelDropDown.NONE) {
    clonedDeckline = changeMultipleEntryFields(clonedDeckline, {
      denominator_kpi_type: null,
      denominator_root_kpi_id: null,
      denominator_kpi_variant_id: null,
      denominator_kpi_id_for_pct: '',
      denominator_kpi_formula: null,
      denominator_kpi_id_drivers: null,
    });
  } else if (newType === PageSidePanelDropDown.BASE_KPI) {
    clonedDeckline = changeMultipleEntryFields(clonedDeckline, {
      denominator_kpi_type: KPIType.BASE,
      denominator_root_kpi_id:
        (wasBase && clonedDeckline.denominator_root_kpi_id) || clonedDeckline.root_kpi_id || null,
      denominator_kpi_variant_id: wasBase ? (clonedDeckline.denominator_kpi_variant_id ?? null) : null,
      denominator_kpi_id_for_pct: wasBase ? clonedDeckline.denominator_kpi_id_for_pct : '',
      denominator_kpi_formula: null,
      denominator_kpi_id_drivers: wasBase ? clonedDeckline.denominator_kpi_id_drivers : null,
    });
  } else if (newType === PageSidePanelDropDown.CALCULATED_KPI) {
    const calculatedFields = {
      denominator_kpi_type: KPIType.CALCULATED,
      denominator_root_kpi_id: null,
      denominator_kpi_variant_id: null,
      denominator_kpi_id_drivers: null,
    };
    if (clonedDeckline.kpi_formula && currentPageKPIDrivers?.account) {
      // Copy existing formula from main KPI to denominator
      const formulaData = {
        ...splitRawFormula(clonedDeckline.kpi_formula),
        account: { isFormula: false, value: currentPageKPIDrivers.account },
      };
      const denominatorFormula = generateFormula(formulaData, rootNodes, ['account']);
      clonedDeckline = changeMultipleEntryFields(clonedDeckline, {
        ...calculatedFields,
        denominator_kpi_id_for_pct: denominatorFormula.id,
        denominator_kpi_formula: denominatorFormula.formula,
      });
    } else {
      // Initialize empty calculated KPI for denominator when main KPI is Base KPI (no formula)
      // CalculatedKPIPanel will derive which dimension should be the formula
      clonedDeckline = changeMultipleEntryFields(clonedDeckline, {
        ...calculatedFields,
        denominator_kpi_id_for_pct: '|Calc|',
        denominator_kpi_formula: '',
      });
    }
  }

  return clonedDeckline;
};

/**
 * Applies page driver changes to a list of decklines (pure function).
 *
 * For BASE_KPI decklines: updates deckline_drivers where the current value matches
 * the previous page driver value, then regenerates kpi_id.
 *
 * For CALCULATED_KPI decklines: updates non-formula dimensions in the kpi_formula
 * where the current value matches the previous page driver value, then regenerates
 * kpi_id and kpi_formula.
 *
 * All other deckline types are returned unchanged.
 *
 * @param decklines - The decklines to update
 * @param prevPageDrivers - The previous page driver values (before the change)
 * @param updates - The changed driver key-value pairs (only keys that differ)
 * @param rootNodes - Root nodes for KPI ID generation
 * @returns Updated decklines array
 */
export const applyPageDriverChangesToDecklines = (
  decklines: EnhancedDeckline[],
  prevPageDrivers: GalaxyKPIDrivers,
  updates: PartialKPIDrivers,
  rootNodes: RootNodesForDimensionsResponse,
): EnhancedDeckline[] => {
  const currentPageDrivers = { ...prevPageDrivers, ...updates };

  return decklines.map((decklineItem): EnhancedDeckline => {
    const decklineItemType = determineDecklineType(decklineItem);

    // BASE_KPI: update deckline_drivers where the value matches the previous page driver
    if (decklineItemType === PageSidePanelDropDown.BASE_KPI && !isNil(decklineItem.deckline_drivers)) {
      // Page-driver propagation is Galaxy-only; narrow to the Galaxy shape (HCTR/empty → skip the row).
      const decklineDrivers = asGalaxyDrivers(decklineItem.deckline_drivers);
      if (isNil(decklineDrivers)) return decklineItem;

      // Only apply updates for dimensions where the deckline's value matched the previous page driver.
      // Dimensions are value+hierarchy pairs (e.g. account + account_hierarchy); if the value key
      // doesn't match, the entire pair is skipped to avoid splitting the dimension.
      const applicableUpdates: PartialKPIDrivers = {};
      Object.keys(updates).forEach((key) => {
        const kpiKey = key as keyof PartialKPIDrivers;
        // Find the dimension config that owns this key (by valueAccessor or hierarchyAccessor)
        const dimensionConfig = Object.values(KPI_DIMENSION_CONFIG_MAP).find(
          (config) =>
            config.decklineDrivers?.valueAccessor === kpiKey || config.decklineDrivers?.hierarchyAccessor === kpiKey,
        );
        // Gate on the dimension's value key — if the deckline's value diverges, skip the entire pair
        const valueKey = (dimensionConfig?.decklineDrivers?.valueAccessor ?? kpiKey) as keyof PartialKPIDrivers;
        // Skip null deckline values — they inherit from page automatically (cascading)
        if (isNil(decklineDrivers[valueKey])) return;
        if (prevPageDrivers[valueKey] === decklineDrivers[valueKey]) {
          applicableUpdates[kpiKey] = updates[kpiKey];
        }
      });

      if (Object.keys(applicableUpdates).length > 0) {
        const newDecklineDrivers = { ...decklineDrivers, ...applicableUpdates };

        // Base-row `kpi_id` is derived by the backend at read from the linked variant and dropped on
        // save — the FE no longer authors it. Clear it to '' (unset) and keep only the local
        // `deckline_drivers` for display/edit continuity.
        return changeMultipleEntryFields(decklineItem, {
          kpi_id: '',
          deckline_drivers: asKPIDrivers(newDecklineDrivers),
        });
      }

      return decklineItem;
    }

    // CALCULATED_KPI: update non-formula dimensions in the kpi_formula
    if (
      decklineItemType === PageSidePanelDropDown.CALCULATED_KPI &&
      !isNil(decklineItem.kpi_formula) &&
      !isEmpty(decklineItem.kpi_formula) &&
      isSupportedFormula(decklineItem.kpi_formula)
    ) {
      try {
        const formulaObject = splitRawFormula(decklineItem.kpi_formula!, currentPageDrivers);

        const shouldUpdateFormula = Object.keys(updates).some((key) => {
          const kpiKey = key as keyof PartialKPIDrivers;
          const formulaEntry = formulaObject[kpiKey];
          return !formulaEntry || (!formulaEntry.isFormula && formulaEntry.value === prevPageDrivers[kpiKey]);
        });

        if (shouldUpdateFormula) {
          const updatedFormulaObject = { ...formulaObject };
          Object.keys(updates).forEach((key) => {
            const kpiKey = key as keyof PartialKPIDrivers;
            const newValue = updates[kpiKey];
            const formulaEntry = updatedFormulaObject[kpiKey];

            if (
              !formulaEntry ||
              (!formulaEntry.isFormula && formulaEntry.value === prevPageDrivers[kpiKey] && newValue)
            ) {
              updatedFormulaObject[kpiKey] = {
                ...formulaEntry,
                value: newValue!,
                isFormula: formulaEntry?.isFormula || false,
              };
            }
          });

          const { id: newKpiId, formula: newKpiFormula } = generateFormula(
            mergePageDriversToParsedFormula(updatedFormulaObject, currentPageDrivers),
            rootNodes,
          );

          return changeMultipleEntryFields(decklineItem, {
            kpi_id: newKpiId,
            kpi_formula: newKpiFormula,
          });
        }
      } catch (error) {
        console.warn('Failed to update calculated KPI formula:', error);
      }

      return decklineItem;
    }

    return decklineItem;
  });
};

/**
 * Applies KPI variant overrides to a deckline's drivers, regenerating kpi_id and is_exception.
 * Used when a KPI variant is updated and the deckline needs to reflect the new overrides.
 *
 * @param deckline - The deckline to update
 * @param variantOverrides - The variant_overrides from the KPI variant
 * @param currentPageKPIDrivers - Current page-level KPI drivers (base for merging)
 * @returns Updated deckline with merged drivers, cleared kpi_id, and updated is_exception
 */
export const applyVariantOverridesToDeckline = (
  deckline: EnhancedDeckline,
  variantOverrides: Partial<GalaxyKPIDrivers>,
  currentPageKPIDrivers: GalaxyKPIDrivers,
): EnhancedDeckline => {
  const mergedDrivers = { ...currentPageKPIDrivers, ...variantOverrides } as GalaxyKPIDrivers;
  const isException = hasDriverMismatch(mergedDrivers, currentPageKPIDrivers);

  // Base-row `kpi_id` is backend-derived (from the variant) and dropped on save — the FE no longer
  // authors it; clear to '' (unset). `deckline_drivers` stays as the local display representation.
  return changeMultipleEntryFields(deckline, {
    deckline_drivers: asKPIDrivers(mergedDrivers),
    kpi_id: '',
    is_exception: isException,
  });
};

/**
 * The deckline updates a page's % denominator label change implies. Turning the label on gives every
 * eligible row that has a root, no denominator, and a dataset denominators support (Galaxy) a base
 * denominator on its own root; turning it off removes every row's denominator. Changing between two
 * labels changes no row.
 *
 * Updates are partial (the backend writes only the fields sent) and fan out to grain siblings there.
 * @param decklines - The page's served decklines (one grain)
 * @param pageId - The page id the decklines belong to
 * @param wasOn - Whether the persisted page had a label
 * @param isOn - Whether the page is saved with a label
 * @returns The deckline updates, empty when the label was neither turned on nor off
 */
export const denominatorLabelRowUpdates = (
  decklines: Deckline[],
  pageId: string,
  wasOn: boolean,
  isOn: boolean,
): UpdateDecklineType[] => {
  if (wasOn === isOn) return [];
  return decklines.flatMap((deckline): UpdateDecklineType[] => {
    const ids = { page_id: pageId, deckline_id: parseIdFromKey(deckline.sk) };
    const currentType = denominatorKpiTypeOf(deckline);
    if (!isOn) {
      return currentType ? [{ ...ids, denominator_kpi_type: null }] : [];
    }
    const eligible =
      !EXCLUDED_DECKLINES_FOR_DENOMINATORS.includes(determineDecklineType(deckline)) &&
      isNil(currentType) &&
      !isEmpty(deckline.root_kpi_id) &&
      DENOMINATOR_DATASETS.includes(datasetSourceFromDrivers(deckline.deckline_drivers));
    return eligible
      ? [{ ...ids, denominator_kpi_type: KPIType.BASE, denominator_root_kpi_id: deckline.root_kpi_id }]
      : [];
  });
};

/**
 * Base-denominator decklines per denominator dataset: the rows already BASE plus the rows `pendingUpdates`
 * make BASE (minus those it clears). A row's denominator dataset is its derived denominator drivers', and
 * Galaxy when it has none, Galaxy being the only dataset denominators support today.
 * @param decklines - The page's served decklines
 * @param pendingUpdates - Deckline updates about to be saved (see {@link denominatorLabelRowUpdates})
 */
export const countBaseDenominatorsByDataset = (
  decklines: Deckline[],
  pendingUpdates: UpdateDecklineType[] = [],
): Partial<Record<DatasetSource, number>> => {
  const pendingById = new Map(pendingUpdates.map((update) => [update.deckline_id, update]));
  return decklines.reduce<Partial<Record<DatasetSource, number>>>((acc, deckline) => {
    const pending = pendingById.get(parseIdFromKey(deckline.sk));
    const type =
      pending && 'denominator_kpi_type' in pending ? pending.denominator_kpi_type : denominatorKpiTypeOf(deckline);
    if (type !== KPIType.BASE) return acc;
    const dataset = isNil(deckline.denominator_kpi_id_drivers)
      ? DatasetSource.GALAXY
      : datasetSourceFromDrivers(deckline.denominator_kpi_id_drivers);
    acc[dataset] = (acc[dataset] ?? 0) + 1;
    return acc;
  }, {});
};
